import moment from 'moment';

/** Same helpers as HotelPerformanceDashboard / LandingOrgDashboard. */
export function getHotelTotalRooms(stats) {
  return Number(stats?.total_rooms ?? stats?.total_inventory ?? 0);
}

export function splitBookingsWithinCapacity(directBooked, onlineBooked, capacity) {
  const direct = Number(directBooked || 0);
  const online = Number(onlineBooked || 0);
  const totalBooked = direct + online;
  const safeCapacity = Math.max(Number(capacity || 0), 0);

  if (totalBooked <= safeCapacity || totalBooked <= 0) {
    return {
      direct_booked: direct,
      online_booked: online,
      overbooked: 0,
    };
  }

  const cappedDirect = Math.min(direct, safeCapacity);
  const cappedOnline = Math.min(online, Math.max(safeCapacity - cappedDirect, 0));

  return {
    direct_booked: cappedDirect,
    online_booked: cappedOnline,
    overbooked: totalBooked - safeCapacity,
  };
}

export function computeOccupancyMetrics(statistics, startDate, endDate) {
  const start = moment(startDate).startOf('day');
  const end = moment(endDate).startOf('day');
  const selectedRangeDays = Math.max(end.diff(start, 'days') + 1, 1);
  const totalRooms = Number(
    statistics?.total_room_capacity ?? ((statistics?.total_inventory || 0) * selectedRangeDays),
  );
  const bookedRooms = Number(statistics?.total_booked_rooms || 0);
  const occupancyPercentage = totalRooms > 0 ? (bookedRooms / totalRooms) * 100 : 0;

  return {
    totalRooms,
    bookedRooms,
    occupancyPercentage,
    selectedRangeDays,
  };
}

export function getDailyStatForDate(orgResponse, dateStr) {
  const dailyStats = orgResponse?.statistics?.daily_stats ?? orgResponse?.daily_stats ?? [];
  return dailyStats.find((entry) => entry.date === dateStr) || dailyStats[dailyStats.length - 1] || {};
}

export function computeDayInventoryBreakdown(statistics, dailyStat) {
  const hotelTotalRooms = getHotelTotalRooms(statistics);
  const dayTotal = Number(dailyStat?.total_hotel_rooms ?? dailyStat?.total ?? hotelTotalRooms ?? 0);
  const rawDirectBooked = Number(dailyStat?.direct_booked || 0);
  const rawOnlineBooked = Number(dailyStat?.online_booked || 0);
  const bookingSplit = splitBookingsWithinCapacity(rawDirectBooked, rawOnlineBooked, dayTotal);
  const booked = Number(
    dailyStat?.rooms_booked ?? dailyStat?.booked_rooms ?? rawDirectBooked + rawOnlineBooked,
  );
  const available = dayTotal > 0 ? Math.max(dayTotal - booked, 0) : 0;

  return {
    dayTotal,
    booked,
    available,
    directBooked: bookingSplit.direct_booked,
    onlineBooked: bookingSplit.online_booked,
    overbooked: bookingSplit.overbooked,
  };
}

export function formatPerformanceCurrency(value) {
  return `₹${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

export function formatPerformancePercent(value) {
  return `${Number(value || 0).toFixed(1)}%`;
}

/** Build today-scoped statistics from a daily_stats row (single-day metrics). */
export function buildTodayStatisticsFromDaily(dailyStat, hotelStatistics) {
  const hotelTotalRooms = getHotelTotalRooms(hotelStatistics);
  const dayTotal = Number(dailyStat?.total_hotel_rooms ?? dailyStat?.total ?? hotelTotalRooms ?? 0);
  const rawDirectBooked = Number(dailyStat?.direct_booked || 0);
  const rawOnlineBooked = Number(dailyStat?.online_booked || 0);
  const booked = Number(
    dailyStat?.rooms_booked ?? dailyStat?.booked_rooms ?? rawDirectBooked + rawOnlineBooked,
  );

  return {
    total_room_capacity: dayTotal,
    total_booked_rooms: booked,
    total_revenue: Number(dailyStat?.revenue || 0),
    total_inventory: dayTotal,
  };
}

/**
 * Derive mobile home dashboard stats from one organization-statistics response (month start → reference date).
 * Avoids a second same-day API round trip.
 */
export function buildMobileHomeDashboardStats(orgResponse, referenceDate = moment().format('YYYY-MM-DD')) {
  const statistics = orgResponse?.statistics || {};
  const monthStart = moment(referenceDate).startOf('month').format('YYYY-MM-DD');
  const mtdMetrics = computeOccupancyMetrics(statistics, monthStart, referenceDate);
  const todayDaily = getDailyStatForDate(orgResponse, referenceDate);
  const todayStatistics = buildTodayStatisticsFromDaily(todayDaily, statistics);
  const todayMetrics = computeOccupancyMetrics(todayStatistics, referenceDate, referenceDate);
  const inventoryBreakdown = computeDayInventoryBreakdown(statistics, todayDaily);

  return {
    bookedRooms: todayMetrics.bookedRooms,
    totalRooms: todayMetrics.totalRooms,
    occupancy: todayMetrics.occupancyPercentage,
    mtdRevenue: statistics.total_revenue || 0,
    mtdExpenses: statistics.total_expense || 0,
    mtdOccupancy: mtdMetrics.occupancyPercentage,
    todayRevenue: todayStatistics.total_revenue || 0,
    directBooked: inventoryBreakdown.directBooked,
    onlineBooked: inventoryBreakdown.onlineBooked,
    available: inventoryBreakdown.available,
    overbooked: inventoryBreakdown.overbooked,
  };
}
